import { MouseEvent, useCallback, useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import Badge from '@mui/material/Badge'
import Collapse from '@mui/material/Collapse'
import Drawer from '@mui/material/Drawer'
import { UserRole } from '@common/interfaces/user'
import cn from 'classnames'
import { toUnreadBadgeProps } from '../../helpers/unreadBadge'
import { ATLAS_PREVIEW_OPT_OUT } from '../../hooks/useGlobalAtlasLinkPreview'
import { useIsTouch } from '../../hooks/useIsMobile'
import { useSheetDismiss } from '../../hooks/useSheetDismiss'
import { useTouchPress } from '../../hooks/useTouchPress'
import { UnreadIndicator, aggregateUnreadIndicator } from '../../redux/channels/unreadIndicator'
import Icon from '../Common/Icon'
import Text from '../Common/Text'
import { IMenuData } from '../SideMenu/SideMenuConfig'
import { useMenuBadgeCounts } from '../SideMenu/useMenuBadgeCounts'
import {
  getInitialExpandableListItemsState,
  updateExpandableListItemsState,
} from '../SideMenu/utils'
import styles from './MobileNav.module.scss'
import { MOBILE_TAB_KEYS, resolveMobileNav } from './mobileNavConfig'
import { usePendingTab } from './usePendingTab'

interface IProps {
  role: UserRole
  /** The role's own menu, already resolved by the caller (client portal vs internal). */
  items: IMenuData[]
}

/**
 * The mobile navigation: a bottom tab bar, plus a sheet for everything that does not fit.
 *
 * Replaces a top-anchored MUI Drawer that had no hamburger — the entire header Toolbar was
 * the toggle, so reading the page title meant opening the menu — and that put navigation
 * in the least reachable 60px of a phone screen. A bottom bar is thumb-reachable and is
 * the shape every native app uses, which is most of what "make it feel like an app" means
 * here.
 *
 * Destinations come from the same per-role `SideMenuConfig` the desktop rail renders, by
 * key, so the two can never drift.
 */
const MobileNav = ({ role, items }: IProps) => {
  const location = useLocation()
  const badgeCounts = useMenuBadgeCounts()
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [expandedItems, setExpandedItems] = useState(() =>
    getInitialExpandableListItemsState(role, location),
  )

  const { tabs, more } = useMemo(
    () => resolveMobileNav(items, MOBILE_TAB_KEYS[role]),
    [items, role],
  )

  const openSheet = useCallback(() => setIsSheetOpen(true), [])
  const closeSheet = useCallback(() => setIsSheetOpen(false), [])

  // Drag the sheet down to dismiss it — the handle below has always said you could.
  const isTouch = useIsTouch()
  const setSheetNode = useSheetDismiss({ isEnabled: isTouch && isSheetOpen, onClose: closeSheet })

  // The press, timed here rather than left to `:active`, which a phone shows too late to
  // see — see `useTouchPress`. Delegated, so one listener covers the whole bar.
  const setBarNode = useTouchPress()
  const setSheetPressNode = useTouchPress()
  const attachSheet = useCallback(
    (node: HTMLElement | null) => {
      setSheetNode(node)
      setSheetPressNode(node)
    },
    [setSheetNode, setSheetPressNode],
  )

  const { pendingKey, markPending } = usePendingTab(location.pathname)

  const handleTabClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      closeSheet()
      markPending(event)
    },
    [closeSheet, markPending],
  )

  const handleExpand = useCallback((label: string) => {
    setExpandedItems((state) => updateExpandableListItemsState(state, label))
  }, [])

  const badgeFor = useCallback(
    (item: IMenuData): UnreadIndicator => (item.badge ? badgeCounts[item.badge] : { kind: 'none' }),
    [badgeCounts],
  )

  // The More tab carries every badge it is hiding, so an unread count can never
  // disappear behind the sheet — collapsing to a dot when all it hides are dots.
  const hiddenBadge = useMemo(() => aggregateUnreadIndicator(more.map(badgeFor)), [more, badgeFor])

  if (tabs.length === 0) {
    return null
  }

  /* A pending tab is the only one that reads as selected: two lit tabs during the wait
     would look like the bar had lost track of where the app is. */
  const renderTab = (item: IMenuData) => (
    <NavLink
      key={item.key}
      to={item.link}
      data-press
      data-nav-key={item.key}
      className={({ isActive }) =>
        cn(styles.tab, {
          [styles.tabActive]: pendingKey ? item.key === pendingKey : isActive,
        })
      }
      onClick={handleTabClick}
    >
      {/* The badge rides the icon rather than the label, so it reads the same in the bar
          as it does on the collapsed desktop rail, where there is no label at all. */}
      <Badge
        {...toUnreadBadgeProps(badgeFor(item))}
        color="error"
        max={99}
        classes={{ badge: styles.badge }}
      >
        <span className={styles.tabIcon}>{item.icon}</span>
      </Badge>
      <Text variant="tab" casing="role" className={styles.tabLabel}>
        {item.label}
      </Text>
    </NavLink>
  )

  return (
    <>
      <nav
        ref={setBarNode}
        className={styles.bar}
        aria-label="Primary"
        data-preload-routes
        {...ATLAS_PREVIEW_OPT_OUT}
      >
        {tabs.map(renderTab)}

        {more.length > 0 && (
          <button
            type="button"
            data-press
            className={cn(styles.tab, { [styles.tabActive]: isSheetOpen })}
            onClick={openSheet}
            aria-label="More destinations"
            aria-expanded={isSheetOpen}
          >
            <Badge
              {...toUnreadBadgeProps(hiddenBadge)}
              color="error"
              max={99}
              classes={{ badge: styles.badge }}
            >
              <span className={styles.tabIcon}>
                <Icon name="more-horizontal" size="md" />
              </span>
            </Badge>
            <Text variant="tab" className={styles.tabLabel}>
              More
            </Text>
          </button>
        )}
      </nav>

      <Drawer
        anchor="bottom"
        open={isSheetOpen}
        onClose={closeSheet}
        classes={{ paper: styles.sheet }}
        slotProps={{ paper: { ref: attachSheet } }}
      >
        {/* A grabber, and now one that grabs: the sheet follows a downward drag and
            leaves if it passes a quarter of its own height. Tapping the scrim still
            works, and remains the only route on a pointer. */}
        <div className={styles.sheetHandle} />

        <div className={styles.sheetItems}>
          {more.map((item) =>
            item.subItems?.length ? (
              <div key={item.key}>
                <button
                  type="button"
                  data-press
                  className={styles.sheetItem}
                  onClick={() => handleExpand(item.label)}
                  aria-expanded={!!expandedItems[item.label]}
                >
                  <span className={styles.sheetIcon}>{item.icon}</span>
                  <span className={styles.sheetLabel}>{item.label}</span>
                </button>
                <Collapse in={!!expandedItems[item.label]} unmountOnExit>
                  {item.subItems.map((subItem) => (
                    <NavLink
                      key={subItem.link}
                      to={subItem.link}
                      data-press
                      className={({ isActive }) =>
                        cn(styles.sheetItem, styles.sheetSubItem, {
                          [styles.sheetItemActive]: isActive,
                        })
                      }
                      onClick={closeSheet}
                    >
                      <span className={styles.sheetLabel}>{subItem.label}</span>
                    </NavLink>
                  ))}
                </Collapse>
              </div>
            ) : (
              <NavLink
                key={item.key}
                to={item.link}
                data-press
                className={({ isActive }) =>
                  cn(styles.sheetItem, { [styles.sheetItemActive]: isActive })
                }
                onClick={closeSheet}
              >
                <span className={styles.sheetIcon}>{item.icon}</span>
                <span className={styles.sheetLabel}>{item.label}</span>
                <Badge
                  {...toUnreadBadgeProps(badgeFor(item))}
                  color="error"
                  max={99}
                  classes={{ badge: styles.badge }}
                />
              </NavLink>
            ),
          )}
        </div>
      </Drawer>
    </>
  )
}

export default MobileNav
